import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Send, X, Sparkles, RotateCcw, ChevronUp, Plus, Zap, Target, MessageSquare, CheckCircle2, Clock, Trash2 } from 'lucide-react';
import { useAI } from '../hooks/useAI';
import { useConversation } from '../contexts/ConversationContext';
import { getLogoBase64 } from '../data/toolData';

// Conversation stages for progress indicator
const STAGES = [
  { id: 'greeting', label: 'Hello', icon: MessageSquare },
  { id: 'understanding', label: 'Understanding', icon: Target },
  { id: 'recommendations', label: 'Recommendations', icon: Zap },
  { id: 'refinement', label: 'Refinement', icon: CheckCircle2 },
];

// Category cards for greeting stage
const CATEGORY_CARDS = [
  {
    id: 'technical',
    label: 'Build Something',
    description: 'Apps, agents, RAG systems',
    message: "I want to build something technical with AI - like an app, agent, or RAG system"
  },
  {
    id: 'automate',
    label: 'Automate Processes',
    description: 'Workflows, tasks, operations',
    message: "I want to automate processes and workflows using AI"
  },
  {
    id: 'integrate',
    label: 'Integrate AI',
    description: 'Add AI to existing business',
    message: "I want to integrate AI into my existing business operations"
  },
];

// Quick reply suggestions based on stage
const QUICK_REPLIES = {
  greeting: [], // Using CATEGORY_CARDS instead
  understanding: [
    { text: "Yes, show me tools" },
    { text: "I need something different" },
    { text: "What about budget?" },
  ],
  recommendations: [
    { text: "Compare top picks" },
    { text: "Show alternatives" },
    { text: "Filter by price" },
  ],
  refinement: [
    { text: "Perfect, I'm done" },
    { text: "Show more options" },
    { text: "Start over" },
  ],
};

// Slash commands
const SLASH_COMMANDS = [
  { command: '/compare', description: 'Compare two tools', action: 'compare' },
  { command: '/budget', description: 'Set budget preference', action: 'budget' },
  { command: '/reset', description: 'Start conversation over', action: 'reset' },
  { command: '/tools', description: 'Show recommended tools', action: 'tools' },
];

// Input suggestions
const INPUT_SUGGESTIONS = [
  "I need a tool for...",
  "What's the best option for...",
  "Compare X with Y",
  "Show me tools under $50/month",
  "I'm a developer looking for...",
];

// ===========================================
// PRELOADED CONVERSATION TREE (Hybrid Mode)
// First 2-3 levels are instant, then AI takes over
// ===========================================

const PRELOADED_GREETING = {
  message: "Hey! Are you here to **build something technical**, **automate processes**, **integrate AI into your business**, or something else?",
  highlightTools: [],
  stage: 'greeting'
};

// Mapping user responses to preloaded follow-ups (first question only)
// After this, the real AI takes over
const PRELOADED_RESPONSES = {
  // Build something technical
  'build something technical': {
    message: "Nice! Tell me more about what you're building - what problem are you trying to solve?",
    highlightTools: [],
    stage: 'understanding'
  },
  'build something': {
    message: "Nice! Tell me more about what you're building - what problem are you trying to solve?",
    highlightTools: [],
    stage: 'understanding'
  },
  'i want to build something technical with ai - like an app, agent, or rag system': {
    message: "Nice! Tell me more about what you're building - what problem are you trying to solve?",
    highlightTools: [],
    stage: 'understanding'
  },
  
  // Automate processes
  'automate processes': {
    message: "Got it! What kind of tasks or workflows are you looking to automate?",
    highlightTools: [],
    stage: 'understanding'
  },
  'automate': {
    message: "Got it! What kind of tasks or workflows are you looking to automate?",
    highlightTools: [],
    stage: 'understanding'
  },
  'i want to automate processes and workflows using ai': {
    message: "Got it! What kind of tasks or workflows are you looking to automate?",
    highlightTools: [],
    stage: 'understanding'
  },
  
  // Integrate AI
  'integrate ai into your business': {
    message: "Cool! What part of your business are you looking to improve with AI?",
    highlightTools: [],
    stage: 'understanding'
  },
  'integrate ai': {
    message: "Cool! What part of your business are you looking to improve with AI?",
    highlightTools: [],
    stage: 'understanding'
  },
  'i want to integrate ai into my existing business operations': {
    message: "Cool! What part of your business are you looking to improve with AI?",
    highlightTools: [],
    stage: 'understanding'
  },
};

/**
 * Check if user message matches a preloaded response
 * Returns the preloaded response or null if no match
 */
function getPreloadedResponse(userMessage) {
  if (!userMessage) return null;
  
  // Normalize the message for matching
  const normalized = userMessage.toLowerCase().trim();
  
  // Direct match
  if (PRELOADED_RESPONSES[normalized]) {
    return PRELOADED_RESPONSES[normalized];
  }
  
  // Partial match - check if any key is contained in the message
  for (const [key, response] of Object.entries(PRELOADED_RESPONSES)) {
    if (normalized.includes(key) || key.includes(normalized)) {
      return response;
    }
  }
  
  return null; // No match - will use real AI
}

/**
 * MessageContent - Renders message with clickable bold options
 * Converts **text** to clickable blue buttons
 */
function MessageContent({ content, onOptionClick, isLatestAssistant = false }) {
  // Parse content and split by **text** patterns
  const parts = [];
  const regex = /\*\*([^*]+)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(content)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: content.slice(lastIndex, match.index)
      });
    }
    // Add the clickable option
    parts.push({
      type: 'option',
      content: match[1]
    });
    lastIndex = regex.lastIndex;
  }
  
  // Add remaining text after last match
  if (lastIndex < content.length) {
    parts.push({
      type: 'text',
      content: content.slice(lastIndex)
    });
  }

  // If no bold patterns found, just return plain text
  if (parts.length === 0) {
    return <span>{content}</span>;
  }

  return (
    <span>
      {parts.map((part, idx) => {
        if (part.type === 'option') {
          return (
            <button
              key={idx}
              onClick={() => onOptionClick?.(part.content)}
              disabled={!isLatestAssistant}
              className={`inline font-medium transition-all ${
                isLatestAssistant 
                  ? 'text-blue-400 hover:text-blue-300 hover:underline cursor-pointer' 
                  : 'text-blue-400/60 cursor-default'
              }`}
            >
              {part.content}
            </button>
          );
        }
        return <span key={idx}>{part.content}</span>;
      })}
    </span>
  );
}

/**
 * TypingReveal - Word-by-word text reveal for AI messages
 */
function TypingReveal({ content, onOptionClick, isLatestAssistant, onComplete }) {
  const [visibleWords, setVisibleWords] = useState(0);
  const words = content.split(/(\s+)/);
  const done = visibleWords >= words.length;

  useEffect(() => {
    if (done) { onComplete?.(); return; }
    const t = setTimeout(() => setVisibleWords(v => v + 1), 30);
    return () => clearTimeout(t);
  }, [visibleWords, done]);

  if (done) {
    return <MessageContent content={content} onOptionClick={onOptionClick} isLatestAssistant={isLatestAssistant} />;
  }

  return <span className="text-zinc-100">{words.slice(0, visibleWords).join('')}<span className="typewriter-cursor text-blue-400 ml-0.5">|</span></span>;
}

/**
 * TypingIndicator - Animated wave effect with text
 */
function TypingIndicator() {
  return (
    <div className="flex items-center gap-3 px-4 py-3 message-slide-left">
      <div className="flex items-center gap-1">
        <div className="typing-wave">
          <span style={{ '--i': 0 }} />
          <span style={{ '--i': 1 }} />
          <span style={{ '--i': 2 }} />
          <span style={{ '--i': 3 }} />
        </div>
      </div>
      <span className="text-sm text-zinc-500 typing-shimmer">Thinking...</span>
    </div>
  );
}

/**
 * ToolCard - Minimal tool chip with real logo
 */
function ToolCard({ toolName, toolData, onAddToStack, onSelectTool }) {
  const tool = toolData?.find(t => t.name.toLowerCase() === toolName.toLowerCase());
  const logoBase64 = tool ? getLogoBase64(tool.name) : null;

  if (!tool) {
    return (
      <span className="text-xs text-zinc-500">{toolName}</span>
    );
  }

  return (
    <button
      onClick={() => onSelectTool?.(tool)}
      className="group flex items-center gap-2 px-2 py-1.5 bg-zinc-800/50 hover:bg-zinc-800 rounded-lg transition-all"
    >
      {/* Tool logo */}
      {logoBase64 ? (
        <img
          src={logoBase64}
          alt={tool.name}
          className="w-5 h-5 rounded object-contain"
          style={{ backgroundColor: tool.bg }}
        />
      ) : (
        <div
          className="w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold text-white"
          style={{ backgroundColor: tool.bg || '#3f3f46' }}
        >
          {tool.name.charAt(0)}
        </div>
      )}

      <span className="text-xs text-zinc-400 group-hover:text-white transition-colors">{tool.name}</span>
    </button>
  );
}

/**
 * ProgressIndicator - Minimal progress bar with stage label
 */
function ProgressIndicator({ currentStage }) {
  const stageIndex = STAGES.findIndex(s => s.id === currentStage);
  const progress = ((stageIndex + 1) / STAGES.length) * 100;
  const currentLabel = STAGES[stageIndex]?.label || 'Hello';
  
  return (
    <div className="flex items-center gap-3">
      {/* Progress bar */}
      <div className="flex-1 h-1 bg-zinc-800 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-teal-500 to-emerald-400 rounded-full transition-all duration-700 ease-out progress-bar-glow"
          style={{ width: `${progress}%` }}
        />
      </div>
      
      {/* Current stage label */}
      <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 whitespace-nowrap">
        <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />
        <span>{currentLabel}</span>
      </div>
    </div>
  );
}

/**
 * CategoryPicker - Large cards for initial category selection
 */
function CategoryPicker({ onSelect, disabled }) {
  return (
    <div className="flex flex-wrap gap-2">
      {CATEGORY_CARDS.map((category, idx) => (
        <button
          key={category.id}
          onClick={() => onSelect(category.message)}
          disabled={disabled}
          className="px-3 py-1.5 bg-zinc-800/30 border border-zinc-700/30 rounded-lg text-sm text-zinc-400 hover:bg-zinc-800/60 hover:border-zinc-600 hover:text-white transition-all disabled:opacity-50"
          style={{ animationDelay: `${idx * 40}ms` }}
        >
          {category.label}
        </button>
      ))}
    </div>
  );
}

/**
 * QuickReplies - Clickable suggestion chips
 */
function QuickReplies({ stage, onSelect, disabled }) {
  // For greeting stage, show category picker
  if (stage === 'greeting') {
    return <CategoryPicker onSelect={onSelect} disabled={disabled} />;
  }
  
  const replies = QUICK_REPLIES[stage] || [];
  
  if (replies.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {replies.map((reply, idx) => (
        <button
          key={idx}
          onClick={() => onSelect(reply.text)}
          disabled={disabled}
          className="quick-reply-chip px-3 py-2 bg-zinc-800/40 border border-zinc-700/40 rounded-lg text-xs text-zinc-400 hover:bg-zinc-800/80 hover:border-teal-500/40 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ animationDelay: `${idx * 50}ms` }}
        >
          {reply.text}
        </button>
      ))}
    </div>
  );
}

/**
 * SlashCommandMenu - Dropdown menu for slash commands
 */
function SlashCommandMenu({ filter, onSelect, onClose }) {
  const filteredCommands = SLASH_COMMANDS.filter(cmd => 
    cmd.command.toLowerCase().includes(filter.toLowerCase())
  );
  
  if (filteredCommands.length === 0) return null;
  
  return (
    <div className="absolute bottom-full left-0 right-0 mb-2 bg-zinc-800 border border-zinc-700 rounded-xl shadow-xl overflow-hidden z-50">
      {filteredCommands.map((cmd, idx) => (
        <button
          key={cmd.command}
          onClick={() => onSelect(cmd)}
          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-700/50 transition-colors text-left"
        >
          <span className="text-teal-400 font-mono text-sm">{cmd.command}</span>
          <span className="text-xs text-zinc-400">{cmd.description}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * InputSuggestions - Auto-complete suggestions
 */
function InputSuggestions({ input, onSelect, visible }) {
  if (!visible || input.length < 2 || input.startsWith('/')) return null;
  
  const suggestions = INPUT_SUGGESTIONS.filter(s => 
    s.toLowerCase().includes(input.toLowerCase())
  ).slice(0, 3);
  
  if (suggestions.length === 0) return null;
  
  return (
    <div className="absolute bottom-full left-0 right-0 mb-2 bg-zinc-800/95 backdrop-blur border border-zinc-700 rounded-xl shadow-xl overflow-hidden z-50">
      {suggestions.map((suggestion, idx) => (
        <button
          key={idx}
          onClick={() => onSelect(suggestion)}
          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-zinc-700/50 transition-colors text-left text-sm text-zinc-300"
        >
          <Sparkles className="w-3 h-3 text-teal-400" />
          {suggestion}
        </button>
      ))}
    </div>
  );
}

/**
 * ConversationOverlay - Enhanced Chat UI
 */
export default function ConversationOverlay({ 
  isOpen, 
  onClose, 
  onHighlightTools,
  onDimTools,
  onProfileUpdate,
  userProfile = null,
  initialMessage = null,
  isDropdown = false,
  anchorRef = null,
  currentPage = 'explore',
  hideInput = false,
  externalInput = '',
  onExternalInputClear = null,
  toolData = [],
  onAddToStack = null,
  onSelectTool = null,
  onHasMessages = null,
}) {
  const { messages, setMessages, currentStage, setCurrentStage, resetMessages, history, activeConversationId, startNewConversation, loadConversation, deleteConversation } = useConversation();
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [editingMessageIdx, setEditingMessageIdx] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [anchorPos, setAnchorPos] = useState({ top: 0, left: 0, width: 0 });
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const { guide, isLoading, checkStatus } = useAI();

  useEffect(() => {
    if (!anchorRef?.current || !isOpen) return;
    const update = () => {
      const r = anchorRef.current.getBoundingClientRect();
      setAnchorPos({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    update();
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
    };
  }, [anchorRef, isOpen]);

  // Handle open/close animations
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      const t = setTimeout(() => setIsAnimating(true), 30);
      return () => clearTimeout(t);
    } else {
      setIsAnimating(false);
      setShowHistory(false);
      const timer = setTimeout(() => setIsVisible(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Notify parent when messages exist (for header styling)
  useEffect(() => {
    onHasMessages?.(messages.length > 0);
  }, [messages.length, onHasMessages]);

  // Initialize with greeting when opened
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      checkStatus();
      startConversation(initialMessage);
    }
  }, [isOpen]);

  // Handle externalInput prop — send as message when it arrives
  const prevExternalInput = useRef('');
  useEffect(() => {
    if (externalInput && externalInput.trim() && externalInput !== prevExternalInput.current && isOpen) {
      prevExternalInput.current = externalInput;
      handleSendMessage(externalInput.trim());
      onExternalInputClear?.();
    }
  }, [externalInput, isOpen]);

  // Listen for external send events (from top search bar)
  useEffect(() => {
    if (!isDropdown) return;
    
    const handleExternalSend = (e) => {
      const message = e.detail;
      if (message && message.trim()) {
        handleSendMessage(message.trim());
      }
    };
    
    window.addEventListener('conversation-send', handleExternalSend);
    return () => window.removeEventListener('conversation-send', handleExternalSend);
  }, [isDropdown, messages]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;
    
    const handleGlobalKeyDown = (e) => {
      // Escape to close
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      
      // Arrow up to edit last message
      if (e.key === 'ArrowUp' && !input && document.activeElement === inputRef.current) {
        const lastUserMsgIdx = [...messages].reverse().findIndex(m => m.role === 'user');
        if (lastUserMsgIdx !== -1) {
          const actualIdx = messages.length - 1 - lastUserMsgIdx;
          setEditingMessageIdx(actualIdx);
          setInput(messages[actualIdx].content);
        }
      }
      
      // Tab to accept first suggestion
      if (e.key === 'Tab' && showSuggestions) {
        e.preventDefault();
        const suggestion = INPUT_SUGGESTIONS.find(s => 
          s.toLowerCase().includes(input.toLowerCase())
        );
        if (suggestion) setInput(suggestion);
        setShowSuggestions(false);
      }
    };
    
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [isOpen, input, messages, showSuggestions, onClose]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 100);
    }
  }, [isOpen]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Detect slash commands
  useEffect(() => {
    if (input.startsWith('/')) {
      setShowSlashMenu(true);
      setShowSuggestions(false);
    } else {
      setShowSlashMenu(false);
      if (input.length >= 2) {
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    }
  }, [input]);

  // Determine stage from last assistant message
  useEffect(() => {
    const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
    if (lastAssistant?.stage) {
      setCurrentStage(lastAssistant.stage);
    }
  }, [messages]);

  const startConversation = async (userMessage = null) => {
    // If no user message, show preloaded greeting (instant)
    if (!userMessage) {
      setMessages([{
        role: 'assistant',
        content: PRELOADED_GREETING.message,
        highlightTools: PRELOADED_GREETING.highlightTools,
        stage: PRELOADED_GREETING.stage
      }]);
      return;
    }

    // Check for preloaded response first (instant)
    const preloaded = getPreloadedResponse(userMessage);
    if (preloaded) {
      setMessages([
        { role: 'user', content: userMessage },
        {
          role: 'assistant',
          content: preloaded.message,
          highlightTools: preloaded.highlightTools || [],
          stage: preloaded.stage || 'understanding'
        }
      ]);
      // Only show recommendations panel for final recommendations stage
      const stage = preloaded.stage || 'understanding';
      if (preloaded.highlightTools?.length > 0 && (stage === 'recommendations' || stage === 'refinement')) {
        onHighlightTools?.(preloaded.highlightTools);
      }
      return;
    }

    // No preloaded match - use real AI
    setIsTyping(true);
    setMessages([{ role: 'user', content: userMessage }]);

    try {
      const response = await guide([{ role: 'user', content: userMessage }], { profile: userProfile, currentPage });
      const stage = response.stage || 'greeting';

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.message,
        highlightTools: response.highlightTools || [],
        stage
      }]);

      // Only show recommendations panel for final recommendations stage
      if (response.highlightTools?.length > 0 && (stage === 'recommendations' || stage === 'refinement')) {
        onHighlightTools?.(response.highlightTools);
      }
      if (response.dimTools?.length > 0) {
        onDimTools?.(response.dimTools);
      }
      if (response.profileUpdates) {
        onProfileUpdate?.(response.profileUpdates);
      }
    } catch (err) {
      console.error('Guide error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Sorry, I'm having trouble connecting. Please try again.",
        highlightTools: [],
        stage: 'error'
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSendMessage = async (userMessage) => {
    if (!userMessage || isLoading) return;

    // If editing a message, replace it
    let newMessages;
    if (editingMessageIdx !== null) {
      newMessages = messages.slice(0, editingMessageIdx);
      newMessages.push({ role: 'user', content: userMessage });
      setEditingMessageIdx(null);
    } else {
      newMessages = [...messages, { role: 'user', content: userMessage }];
    }

    // Check for preloaded response first (instant!)
    const preloaded = getPreloadedResponse(userMessage);
    if (preloaded) {
      const stage = preloaded.stage || 'understanding';
      setMessages([...newMessages, {
        role: 'assistant',
        content: preloaded.message,
        highlightTools: preloaded.highlightTools || [],
        stage
      }]);
      // Only show recommendations panel for final recommendations stage
      if (preloaded.highlightTools?.length > 0 && (stage === 'recommendations' || stage === 'refinement')) {
        onHighlightTools?.(preloaded.highlightTools);
      }
      return; // Don't call AI
    }

    // No preloaded match - use real AI
    setMessages(newMessages);
    setIsTyping(true);

    try {
      const apiMessages = newMessages.map(m => ({
        role: m.role,
        content: m.content
      }));

      const response = await guide(apiMessages, { profile: userProfile, currentPage });
      const stage = response.stage || 'understanding';

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.message,
        highlightTools: response.highlightTools || [],
        stage
      }]);

      // Only show recommendations panel for final recommendations stage
      if (response.highlightTools?.length > 0 && (stage === 'recommendations' || stage === 'refinement')) {
        onHighlightTools?.(response.highlightTools);
      }
      if (response.dimTools?.length > 0) {
        onDimTools?.(response.dimTools);
      }
      if (response.profileUpdates) {
        onProfileUpdate?.(response.profileUpdates);
      }
    } catch (err) {
      console.error('Guide error:', err);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    
    const userMessage = input.trim();
    setInput('');
    setShowSlashMenu(false);
    setShowSuggestions(false);
    handleSendMessage(userMessage);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSlashCommand = (cmd) => {
    setShowSlashMenu(false);
    setInput('');
    
    switch (cmd.action) {
      case 'reset':
        resetConversation();
        break;
      case 'budget':
        handleSendMessage("What are my budget options?");
        break;
      case 'compare':
        handleSendMessage("I'd like to compare some tools");
        break;
      case 'tools':
        handleSendMessage("Show me your recommendations");
        break;
    }
  };

  const handleQuickReply = (reply) => {
    if (reply === "Start over") {
      resetConversation();
    } else {
      handleSendMessage(reply);
    }
  };

  const resetConversation = () => {
    startNewConversation();
    onHighlightTools?.([]);
    onDimTools?.([]);
    startConversation();
  };

  // Get last messages for preview and quick replies
  const lastAssistantMsg = [...messages].reverse().find(m => m.role === 'assistant');
  const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');

  // Dropdown style - appears below search bar
  if (isDropdown) {
    if (!isVisible) return null;

    const isFirstAssistant = (idx) => {
      for (let i = 0; i < idx; i++) { if (messages[i].role === 'assistant') return false; }
      return true;
    };
    const isLatest = (idx) => idx === messages.length - 1 || (idx === messages.length - 2 && messages[messages.length - 1]?.role === 'user');

    return (
      <div
        className={`chat-container absolute top-full left-0 right-0 mt-1 z-[9999] overflow-hidden transition-all ease-out ${
          isAnimating
            ? 'opacity-100 translate-y-0 scale-100 duration-400'
            : 'opacity-0 -translate-y-4 scale-[0.98] pointer-events-none duration-200'
        }`}
        style={{ transformOrigin: 'top center' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Gradient vignette edges — no hard border */}
        <div className="absolute inset-0 pointer-events-none z-10">
          <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-[#09090b]/70 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#09090b]/70 to-transparent" />
          <div className="absolute top-0 bottom-0 left-0 w-6 bg-gradient-to-r from-[#09090b]/70 to-transparent" />
          <div className="absolute top-0 bottom-0 right-0 w-6 bg-gradient-to-l from-[#09090b]/70 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative bg-[#09090b] rounded-2xl border border-white/25 flex flex-col" style={{ maxHeight: 'calc(100vh - 14rem)' }}>

          {/* Minimal progress */}
          <div className="relative px-5 pt-4 pb-2 flex-shrink-0">
            <ProgressIndicator currentStage={currentStage} />
          </div>

          {/* Messages */}
          <div className="relative flex-1 min-h-0 overflow-y-auto px-5 py-3 space-y-4 scrollbar-thin">
            {messages.map((message, idx) => (
              <div
                key={idx}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {message.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                    <Sparkles className="w-3.5 h-3.5 text-blue-400" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] ${
                    message.role === 'user'
                      ? 'message-slide-right px-4 py-2.5 rounded-2xl rounded-br-md bg-blue-500/10 border border-blue-500/20 text-white'
                      : 'message-slide-left text-zinc-200'
                  } ${editingMessageIdx === idx ? 'ring-1 ring-blue-400/40' : ''}`}
                >
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {message.role === 'assistant' ? (
                      isLatest(idx) ? (
                        <TypingReveal
                          content={message.content}
                          onOptionClick={(option) => handleSendMessage(option)}
                          isLatestAssistant={true}
                        />
                      ) : (
                        <MessageContent
                          content={message.content}
                          onOptionClick={(option) => handleSendMessage(option)}
                          isLatestAssistant={isLatest(idx)}
                        />
                      )
                    ) : (
                      message.content
                    )}
                  </p>

                  {message.highlightTools?.length > 0 && message.stage === 'recommendations' && (
                    <div className="flex flex-wrap gap-1.5 mt-3 pt-2 border-t border-white/[0.04]">
                      {message.highlightTools.slice(0, 4).map((toolName, i) => (
                        <ToolCard
                          key={i}
                          toolName={toolName}
                          toolData={toolData}
                          onAddToStack={onAddToStack}
                          onSelectTool={onSelectTool}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="flex justify-start">
                <div className="w-7 h-7 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                  <Sparkles className="w-3.5 h-3.5 text-blue-400" />
                </div>
                <TypingIndicator />
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick replies */}
          {!isTyping && lastAssistantMsg && (
            <div className="px-5 pb-3">
              <QuickReplies
                stage={currentStage}
                onSelect={handleQuickReply}
                disabled={isLoading}
              />
            </div>
          )}

          {/* Input area */}
          <div className="relative px-5 pb-4 pt-2 flex-shrink-0">
            {!hideInput && showSlashMenu && (
              <SlashCommandMenu
                filter={input.slice(1)}
                onSelect={handleSlashCommand}
                onClose={() => setShowSlashMenu(false)}
              />
            )}
            {!hideInput && (
              <InputSuggestions
                input={input}
                onSelect={(s) => { setInput(s); setShowSuggestions(false); }}
                visible={showSuggestions}
              />
            )}

            {/* Chat history overlay */}
            {showHistory && (
              <>
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]" onClick={() => setShowHistory(false)} />
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
                  <div className="pointer-events-auto w-full max-w-2xl max-h-[85vh] flex flex-col bg-[#0c0c0e]/98 backdrop-blur-2xl rounded-2xl border border-white/[0.08] shadow-2xl overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.06]">
                      <div>
                        <h3 className="text-lg font-semibold text-white">Chat History</h3>
                        <p className="text-sm text-zinc-500 mt-0.5">{history.length} conversation{history.length !== 1 ? 's' : ''}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => { startNewConversation(); setShowHistory(false); startConversation(); }}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 transition-colors"
                        >
                          <Plus className="w-4 h-4" /> New chat
                        </button>
                        <button
                          onClick={() => setShowHistory(false)}
                          className="p-2 hover:bg-white/[0.06] rounded-lg text-zinc-500 hover:text-zinc-300 transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                      {history.length === 0 ? (
                        <div className="px-6 py-20 text-center">
                          <Clock className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
                          <p className="text-base text-zinc-400">No past conversations yet</p>
                          <p className="text-sm text-zinc-600 mt-1">Your chats will appear here automatically</p>
                        </div>
                      ) : (
                        <div className="p-3 space-y-1">
                          {history.map(conv => (
                            <div
                              key={conv.id}
                              className={`group flex items-start gap-4 px-5 py-4 rounded-xl cursor-pointer transition-all ${
                                conv.id === activeConversationId
                                  ? 'bg-blue-500/10 ring-1 ring-blue-500/20'
                                  : 'hover:bg-white/[0.04]'
                              }`}
                              onClick={() => { loadConversation(conv.id); setShowHistory(false); }}
                            >
                              <div className={`mt-0.5 p-2 rounded-lg flex-shrink-0 ${
                                conv.id === activeConversationId ? 'bg-blue-500/20 text-blue-400' : 'bg-white/[0.04] text-zinc-500'
                              }`}>
                                <MessageSquare className="w-5 h-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className={`text-base font-medium truncate ${conv.id === activeConversationId ? 'text-blue-300' : 'text-zinc-200'}`}>
                                  {conv.title}
                                </div>
                                <div className="text-sm text-zinc-500 mt-1">
                                  {new Date(conv.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                  {' · '}
                                  {conv.messages.filter(m => m.role === 'user').length} messages
                                </div>
                              </div>
                              <button
                                onClick={(e) => { e.stopPropagation(); deleteConversation(conv.id); }}
                                className="opacity-0 group-hover:opacity-100 mt-1 p-2 hover:bg-red-500/15 rounded-lg text-zinc-600 hover:text-red-400 transition-all"
                                title="Delete conversation"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {hideInput ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className={`p-2 rounded-lg transition-colors ${
                    showHistory ? 'bg-blue-500/15 text-blue-400' : 'hover:bg-white/[0.05] text-zinc-600 hover:text-zinc-300'
                  }`}
                  title="Chat history"
                >
                  <Clock className="w-4 h-4" />
                </button>
                <button
                  onClick={resetConversation}
                  className="p-2 hover:bg-white/[0.05] rounded-lg text-zinc-600 hover:text-zinc-300 transition-colors"
                  title="New chat"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-1.5 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className={`p-2 rounded-lg transition-colors ${
                    showHistory ? 'bg-blue-500/15 text-blue-400' : 'hover:bg-white/[0.05] text-zinc-600 hover:text-zinc-300'
                  }`}
                  title="Chat history"
                >
                  <Clock className="w-4 h-4" />
                </button>

                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask anything..."
                  className="flex-1 px-3 py-2 bg-transparent text-white text-sm placeholder-zinc-600 focus:outline-none"
                  autoFocus
                />

                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                  className={`p-2.5 rounded-lg transition-all ${
                    input.trim() && !isLoading
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/30'
                      : 'bg-white/[0.03] text-zinc-700 cursor-not-allowed'
                  }`}
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Original overlay style (fallback)
  if (!isVisible) return null;
  
  return (
    <div className="absolute inset-0 z-40">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-x-0 top-40 bottom-8 max-w-xl mx-auto flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
          {messages.map((message, idx) => (
            <div key={idx} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-4 py-3 rounded-2xl ${message.role === 'user' ? 'bg-teal-500/20 border border-teal-500/30' : 'bg-zinc-800/90 border border-zinc-700/50'}`}>
                <p className="text-sm">
                  {message.role === 'assistant' ? (
                    <MessageContent 
                      content={message.content} 
                      onOptionClick={(option) => handleSendMessage(option)}
                      isLatestAssistant={idx === messages.length - 1 || (idx === messages.length - 2 && messages[messages.length - 1]?.role === 'user')}
                    />
                  ) : (
                    message.content
                  )}
                </p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
        <div className="p-4">
          <div className="relative flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-2xl p-2">
            {showSlashMenu && (
              <SlashCommandMenu 
                filter={input.slice(1)} 
                onSelect={handleSlashCommand}
                onClose={() => setShowSlashMenu(false)}
              />
            )}
            <InputSuggestions
              input={input}
              onSelect={(s) => { setInput(s); setShowSuggestions(false); }}
              visible={showSuggestions}
            />
            <input 
              ref={inputRef} 
              type="text" 
              value={input} 
              onChange={(e) => setInput(e.target.value)} 
              onKeyDown={handleKeyDown} 
              placeholder="Type... (/ for commands)" 
              className="flex-1 px-3 py-2 bg-transparent text-white focus:outline-none" 
            />
            <button onClick={handleSend} disabled={!input.trim()} className="p-2 bg-teal-500 rounded-lg text-white"><Send className="w-4 h-4" /></button>
            <button onClick={onClose} className="p-2 text-zinc-400"><X className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
